import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, ClockIcon, FilterIcon, SearchIcon, SortAscIcon, TagIcon, XCircleIcon, AlertTriangleIcon, CalendarIcon, RefreshCwIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, ArrowUpCircleIcon, ArrowDownCircleIcon, ClipboardListIcon, ChevronLeftIcon, ChevronRightIcon, ArrowLeftIcon, EyeIcon, UserIcon, UsersIcon } from 'lucide-react';
import { DIExceptionHandling } from './DIExceptionHandling';
import { DocumentManagementScreen } from './DocumentManagementScreen';
import { getAllTasks, Task } from './sharedTaskData';
// Task status types
type TaskStatus = 'Pending' | 'In Progress' | 'Completed' | 'Cancelled' | 'Overdue' | 'Waiting Approval';
// Task priority types
type TaskPriority = 'Low' | 'Medium' | 'High' | 'Urgent';
// Module types
type TaskModule = 'PO' | 'VM' | 'AP' | 'CM' | 'EM' | 'DI' | 'OCR' | 'Financial' | 'Settings';
// Task type
type TaskType = 'Review & Confirm' | 'Action Required' | 'Document Review';
interface TasksManagementProps {
  locale?: string;
  onViewChange?: (view: 'list' | 'task-detail') => void;
}
export function TasksManagement({
  locale = 'en-US',
  onViewChange
}: TasksManagementProps) {
  // State for tasks and filters
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Task viewing state
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [currentView, setCurrentView] = useState<'list' | 'task-detail'>('list');
  // Filter states
  const [filters, setFilters] = useState({
    modules: [] as TaskModule[],
    statuses: [] as TaskStatus[],
    priorities: [] as TaskPriority[],
    taskTypes: [] as TaskType[],
    dateRange: '',
    startDate: '',
    endDate: '',
    tags: [] as string[],
    assignedTo: [] as string[],
    overdueOnly: false,
    assignedToMe: false
  });
  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Task;
    direction: 'ascending' | 'descending';
  }>({
    key: 'dueDate',
    direction: 'ascending'
  });
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  // Notify parent component when view changes
  useEffect(() => {
    if (onViewChange) {
      onViewChange(currentView);
    }
  }, [currentView, onViewChange]);
  // Initialize tasks on component mount - using shared data source
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      const allTasks = getAllTasks();
      setTasks(allTasks);
      setFilteredTasks(allTasks);
      setIsLoading(false);
    }, 800);
  }, []);
  // Update filtered tasks when filters or search term changes
  useEffect(() => {
    let result = [...tasks];
    // Apply search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(task => task.title.toLowerCase().includes(searchLower) || task.description.toLowerCase().includes(searchLower) || task.relatedEntityName && task.relatedEntityName.toLowerCase().includes(searchLower) || task.tags && task.tags.some(tag => tag.toLowerCase().includes(searchLower)));
    }
    // Apply module filters
    if (filters.modules.length > 0) {
      result = result.filter(task => filters.modules.includes(task.module));
    }
    // Apply status filters
    if (filters.statuses.length > 0) {
      result = result.filter(task => filters.statuses.includes(task.status));
    }
    // Apply priority filters
    if (filters.priorities.length > 0) {
      result = result.filter(task => filters.priorities.includes(task.priority));
    }
    // Apply task type filters
    if (filters.taskTypes.length > 0) {
      result = result.filter(task => filters.taskTypes.includes(task.taskType));
    }
    // Apply overdue filter
    if (filters.overdueOnly) {
      result = result.filter(task => task.status === 'Overdue');
    }
    // Apply assigned to me filter
    if (filters.assignedToMe) {
      result = result.filter(task => task.assignedTo === 'Admin');
    }
    // Apply date range filter
    if (filters.dateRange) {
      const now = new Date();
      let startDate = new Date();
      if (filters.dateRange === 'today') {
        startDate.setHours(0, 0, 0, 0);
      } else if (filters.dateRange === 'tomorrow') {
        startDate.setDate(now.getDate() + 1);
        startDate.setHours(0, 0, 0, 0);
      } else if (filters.dateRange === 'this-week') {
        const day = now.getDay();
        startDate.setDate(now.getDate() - day);
        startDate.setHours(0, 0, 0, 0);
      } else if (filters.dateRange === 'next-week') {
        const day = now.getDay();
        startDate.setDate(now.getDate() - day + 7);
        startDate.setHours(0, 0, 0, 0);
      } else if (filters.dateRange === 'this-month') {
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
      } else if (filters.dateRange === 'custom') {
        if (filters.startDate && filters.endDate) {
          const customStartDate = new Date(filters.startDate);
          const customEndDate = new Date(filters.endDate);
          customEndDate.setHours(23, 59, 59, 999);
          result = result.filter(task => {
            const taskDueDate = new Date(task.dueDate);
            return taskDueDate >= customStartDate && taskDueDate <= customEndDate;
          });
          setFilteredTasks(result);
          return;
        }
      }
      result = result.filter(task => {
        const taskDueDate = new Date(task.dueDate);
        return taskDueDate >= startDate;
      });
    }
    // Apply tag filters
    if (filters.tags.length > 0) {
      result = result.filter(task => task.tags && filters.tags.some(tag => task.tags?.includes(tag)));
    }
    // Apply assigned to filters
    if (filters.assignedTo.length > 0) {
      result = result.filter(task => filters.assignedTo.includes(task.assignedTo));
    }
    // Apply sorting
    result.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    setFilteredTasks(result);
    setCurrentPage(1);
  }, [tasks, searchTerm, filters, sortConfig]);
  // Update total pages when filtered tasks or page size changes
  useEffect(() => {
    setTotalPages(Math.ceil(filteredTasks.length / pageSize));
  }, [filteredTasks, pageSize]);
  // Get current page tasks
  const getCurrentPageTasks = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredTasks.slice(startIndex, endIndex);
  };
  // Handle sort request
  const requestSort = (key: keyof Task) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({
      key,
      direction
    });
  };
  // Toggle filter for modules, statuses, priorities, and task types
  const toggleFilter = (filterType: 'modules' | 'statuses' | 'priorities' | 'taskTypes', value: TaskModule | TaskStatus | TaskPriority | TaskType) => {
    setFilters(prev => {
      const currentFilters = prev[filterType] as any[];
      return {
        ...prev,
        [filterType]: currentFilters.includes(value) ? currentFilters.filter(f => f !== value) : [...currentFilters, value]
      };
    });
  };
  // Toggle tag filter
  const toggleTagFilter = (tag: string) => {
    setFilters(prev => {
      const currentTags = prev.tags;
      return {
        ...prev,
        tags: currentTags.includes(tag) ? currentTags.filter(t => t !== tag) : [...currentTags, tag]
      };
    });
  };
  // Toggle assigned to filter
  const toggleAssignedToFilter = (assignee: string) => {
    setFilters(prev => {
      const currentAssignedTo = prev.assignedTo;
      return {
        ...prev,
        assignedTo: currentAssignedTo.includes(assignee) ? currentAssignedTo.filter(a => a !== assignee) : [...currentAssignedTo, assignee]
      };
    });
  };
  // Reset all filters
  const resetFilters = () => {
    setFilters({
      modules: [],
      statuses: [],
      priorities: [],
      taskTypes: [],
      dateRange: '',
      startDate: '',
      endDate: '',
      tags: [],
      assignedTo: [],
      overdueOnly: false,
      assignedToMe: false
    });
    setSearchTerm('');
  };
  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  // Format date based on locale
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Check if a task is overdue
  const isOverdue = (dueDate: string) => {
    const now = new Date();
    const taskDueDate = new Date(dueDate);
    return taskDueDate < now;
  };
  // Get all available tags from tasks
  const getAllTags = () => {
    const tagSet = new Set<string>();
    tasks.forEach(task => {
      if (task.tags) {
        task.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet);
  };
  // Get all unique assignees (users and groups)
  const getAllAssignees = () => {
    const assigneeSet = new Set<string>();
    tasks.forEach(task => {
      if (task.assignedTo) {
        assigneeSet.add(task.assignedTo);
      }
    });
    return Array.from(assigneeSet).sort();
  };
  // Get status badge color and icon
  const getStatusBadge = (status: TaskStatus) => {
    switch (status) {
      case 'Completed':
        return {
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          icon: <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-6867" />
        };
      case 'In Progress':
        return {
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          icon: <ClockIcon className="h-4 w-4 text-blue-500 mr-1" data-id="element-6868" />
        };
      case 'Pending':
        return {
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          icon: <ClockIcon className="h-4 w-4 text-yellow-500 mr-1" data-id="element-6869" />
        };
      case 'Cancelled':
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          icon: <XCircleIcon className="h-4 w-4 text-gray-500 mr-1" data-id="element-6870" />
        };
      case 'Overdue':
        return {
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          icon: <AlertTriangleIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-6871" />
        };
      case 'Waiting Approval':
        return {
          bgColor: 'bg-purple-100',
          textColor: 'text-purple-800',
          icon: <ClockIcon className="h-4 w-4 text-purple-500 mr-1" data-id="element-6872" />
        };
      default:
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          icon: null
        };
    }
  };
  // Get priority badge color and icon
  const getPriorityBadge = (priority: TaskPriority) => {
    switch (priority) {
      case 'Urgent':
        return {
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          icon: <ArrowUpCircleIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-6873" />
        };
      case 'High':
        return {
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-800',
          icon: <ArrowUpCircleIcon className="h-4 w-4 text-orange-500 mr-1" data-id="element-6874" />
        };
      case 'Medium':
        return {
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          icon: null
        };
      case 'Low':
        return {
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          icon: <ArrowDownCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-6875" />
        };
      default:
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          icon: null
        };
    }
  };
  // Get module badge color
  const getModuleBadge = (module: TaskModule) => {
    switch (module) {
      case 'PO':
        return 'bg-indigo-100 text-indigo-800';
      case 'VM':
        return 'bg-purple-100 text-purple-800';
      case 'AP':
        return 'bg-blue-100 text-blue-800';
      case 'CM':
        return 'bg-teal-100 text-teal-800';
      case 'EM':
        return 'bg-emerald-100 text-emerald-800';
      case 'DI':
        return 'bg-amber-100 text-amber-800';
      case 'OCR':
        return 'bg-fuchsia-100 text-fuchsia-800';
      case 'Financial':
        return 'bg-cyan-100 text-cyan-800';
      case 'Settings':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get task type badge color
  const getTaskTypeBadge = (taskType: TaskType) => {
    switch (taskType) {
      case 'Document Review':
        return 'bg-amber-100 text-amber-800';
      case 'Action Required':
        return 'bg-red-100 text-red-800';
      case 'Review & Confirm':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Render assigned to badge with icon for user or group
  const renderAssignedToBadge = (task: Task) => {
    const isGroup = task.assignedToType === 'group';
    const Icon = isGroup ? UsersIcon : UserIcon;
    const bgColor = isGroup ? 'bg-purple-50' : 'bg-blue-50';
    const textColor = isGroup ? 'text-purple-700' : 'text-blue-700';
    const iconColor = isGroup ? 'text-purple-500' : 'text-blue-500';
    // Extract name from email if it's an email address, otherwise use as-is
    const displayName = task.assignedTo.includes('@') ? task.assignedTo.split('@')[0].replace(/[._]/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : task.assignedTo;
    return <div className={`inline-flex items-center px-2.5 py-1 rounded-md ${bgColor}`} data-id="element-6876">
        <Icon className={`h-3.5 w-3.5 mr-1.5 ${iconColor}`} data-id="element-6877" />
        <span className={`text-sm font-medium ${textColor}`} data-id="element-6878">
          {displayName}
        </span>
      </div>;
  };
  // Handle task completion
  const handleCompleteTask = (taskId: string) => {
    setTasks(prevTasks => prevTasks.map(task => task.id === taskId ? {
      ...task,
      status: 'Completed' as TaskStatus,
      completedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    } : task));
  };
  // Handle task cancellation
  const handleCancelTask = (taskId: string) => {
    setTasks(prevTasks => prevTasks.map(task => task.id === taskId ? {
      ...task,
      status: 'Cancelled' as TaskStatus,
      updatedAt: new Date().toISOString()
    } : task));
  };
  // Handle task click - open appropriate view based on task type
  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setCurrentView('task-detail');
  };
  // Handle back to task list
  const handleBackToList = () => {
    setSelectedTask(null);
    setCurrentView('list');
  };
  // Render task detail view based on task type
  const renderTaskDetail = () => {
    if (!selectedTask) return null;
    // If it's a Document Review task, show the DocumentManagementScreen component
    if (selectedTask.taskType === 'Document Review') {
      return <div data-id="element-6879">
          <div className="mb-6 flex items-center" data-id="element-6880">
            <button onClick={handleBackToList} className="mr-4 p-2 text-gray-400 hover:text-gray-500 rounded-md hover:bg-gray-100" data-id="element-6881">
              <ArrowLeftIcon className="h-5 w-5" data-id="element-6882" />
            </button>
            <div data-id="element-6883">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-6884">
                {selectedTask.title}
              </h2>
              <p className="mt-1 text-sm text-gray-500" data-id="element-6885">
                {selectedTask.description}
              </p>
            </div>
          </div>
          <DocumentManagementScreen data-id="element-6886" />
        </div>;
    }
    // Generic task detail view for other task types
    return <div className="space-y-6" data-id="element-6887">
        <div className="flex items-center" data-id="element-6888">
          <button onClick={handleBackToList} className="mr-4 p-2 text-gray-400 hover:text-gray-500 rounded-md hover:bg-gray-100" data-id="element-6889">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-6890" />
          </button>
          <div data-id="element-6891">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-6892">
              {selectedTask.title}
            </h2>
            <p className="mt-1 text-sm text-gray-500" data-id="element-6893">
              Task ID: {selectedTask.id}
            </p>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-6894">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-6895">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6896">
              Task Details
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-6897">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2" data-id="element-6898">
              <div data-id="element-6899">
                <dt className="text-sm font-medium text-gray-500" data-id="element-6900">
                  Description
                </dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-6901">
                  {selectedTask.description}
                </dd>
              </div>
              <div data-id="element-6902">
                <dt className="text-sm font-medium text-gray-500" data-id="element-6903">Task Type</dt>
                <dd className="mt-1" data-id="element-6904">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTaskTypeBadge(selectedTask.taskType)}`} data-id="element-6905">
                    {selectedTask.taskType}
                  </span>
                </dd>
              </div>
              <div data-id="element-6906">
                <dt className="text-sm font-medium text-gray-500" data-id="element-6907">Module</dt>
                <dd className="mt-1" data-id="element-6908">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getModuleBadge(selectedTask.module)}`} data-id="element-6909">
                    {selectedTask.module}
                  </span>
                </dd>
              </div>
              <div data-id="element-6910">
                <dt className="text-sm font-medium text-gray-500" data-id="element-6911">Status</dt>
                <dd className="mt-1" data-id="element-6912">
                  {(() => {
                  const statusBadge = getStatusBadge(selectedTask.status);
                  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.bgColor} ${statusBadge.textColor}`} data-id="element-6913">
                        {statusBadge.icon}
                        {selectedTask.status}
                      </span>;
                })()}
                </dd>
              </div>
              <div data-id="element-6914">
                <dt className="text-sm font-medium text-gray-500" data-id="element-6915">Priority</dt>
                <dd className="mt-1" data-id="element-6916">
                  {(() => {
                  const priorityBadge = getPriorityBadge(selectedTask.priority);
                  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityBadge.bgColor} ${priorityBadge.textColor}`} data-id="element-6917">
                        {priorityBadge.icon}
                        {selectedTask.priority}
                      </span>;
                })()}
                </dd>
              </div>
              <div data-id="element-6918">
                <dt className="text-sm font-medium text-gray-500" data-id="element-6919">Due Date</dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-6920">
                  {formatDate(selectedTask.dueDate)}
                </dd>
              </div>
              <div data-id="element-6921">
                <dt className="text-sm font-medium text-gray-500" data-id="element-6922">
                  Assigned To
                </dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-6923">
                  {renderAssignedToBadge(selectedTask)}
                </dd>
              </div>
              <div data-id="element-6924">
                <dt className="text-sm font-medium text-gray-500" data-id="element-6925">
                  Assigned By
                </dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-6926">
                  {selectedTask.assignedBy}
                </dd>
              </div>
              <div data-id="element-6927">
                <dt className="text-sm font-medium text-gray-500" data-id="element-6928">Created</dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-6929">
                  {formatDate(selectedTask.createdAt)}
                </dd>
              </div>
              {selectedTask.relatedEntityName && <div className="sm:col-span-2" data-id="element-6930">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-6931">
                    Related Entity
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900" data-id="element-6932">
                    {selectedTask.relatedEntityType}:{' '}
                    {selectedTask.relatedEntityName}
                  </dd>
                </div>}
              {selectedTask.tags && selectedTask.tags.length > 0 && <div className="sm:col-span-2" data-id="element-6933">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-6934">Tags</dt>
                  <dd className="mt-1 flex flex-wrap gap-2" data-id="element-6935">
                    {selectedTask.tags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-6936">
                        <TagIcon className="h-3 w-3 mr-0.5" data-id="element-6937" />
                        {tag}
                      </span>)}
                  </dd>
                </div>}
            </dl>
          </div>
          <div className="px-4 py-4 sm:px-6 bg-gray-50 border-t border-gray-200" data-id="element-6938">
            <div className="flex justify-end space-x-3" data-id="element-6939">
              {selectedTask.status !== 'Completed' && selectedTask.status !== 'Cancelled' && <>
                    <button onClick={() => {
                handleCompleteTask(selectedTask.id);
                handleBackToList();
              }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-6940">
                      <CheckIcon className="h-4 w-4 mr-2" data-id="element-6941" />
                      Mark Complete
                    </button>
                    <button onClick={() => {
                handleCancelTask(selectedTask.id);
                handleBackToList();
              }} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6942">
                      <XIcon className="h-4 w-4 mr-2" data-id="element-6943" />
                      Cancel Task
                    </button>
                  </>}
            </div>
          </div>
        </div>
      </div>;
  };
  // If viewing a task, render the task detail view
  if (currentView === 'task-detail') {
    return renderTaskDetail();
  }
  // Otherwise render the task list
  return <div className="space-y-6" data-id="element-6944">
      <div className="flex justify-between items-center" data-id="element-6945">
        <div data-id="element-6946">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-6947">Tasks</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6948">
            View and manage your tasks across all modules
          </p>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-6949">
        <div className="px-4 py-5 sm:p-6" data-id="element-6950">
          {/* Search and filter controls */}
          <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-4 mb-6" data-id="element-6951">
            <div className="relative flex-1 max-w-lg" data-id="element-6952">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6953">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-6954" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Search tasks by title, description, or tags..." data-id="element-6955" />
            </div>
            <div className="flex space-x-2" data-id="element-6956">
              <button onClick={() => setShowFilters(!showFilters)} className={`inline-flex items-center px-3 py-2 border ${showFilters ? 'border-indigo-500 text-indigo-700' : 'border-gray-300 text-gray-700'} rounded-md shadow-sm text-sm font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-6957">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-6958" />
                Filters
                {(filters.modules.length > 0 || filters.statuses.length > 0 || filters.priorities.length > 0 || filters.taskTypes.length > 0 || filters.tags.length > 0 || filters.assignedTo.length > 0 || filters.dateRange || filters.overdueOnly || !filters.assignedToMe) && ` (${filters.modules.length + filters.statuses.length + filters.priorities.length + filters.taskTypes.length + filters.tags.length + filters.assignedTo.length + (filters.dateRange ? 1 : 0) + (filters.overdueOnly ? 1 : 0) + (!filters.assignedToMe ? 1 : 0)})`}
              </button>
              {(filters.modules.length > 0 || filters.statuses.length > 0 || filters.priorities.length > 0 || filters.taskTypes.length > 0 || filters.tags.length > 0 || filters.assignedTo.length > 0 || filters.dateRange || filters.overdueOnly || !filters.assignedToMe || searchTerm) && <button onClick={resetFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6959">
                  <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-6960" />
                  Reset
                </button>}
            </div>
          </div>

          {/* Filter panel */}
          {showFilters && <div className="bg-gray-50 p-4 rounded-md mb-6" data-id="element-6961">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-6962">
                {/* Task Type filters */}
                <div data-id="element-6963">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-6964">
                    Task Type
                  </h4>
                  <div className="space-y-2" data-id="element-6965">
                    {(['Review & Confirm', 'Action Required', 'Document Review'] as TaskType[]).map(type => <div key={type} className="flex items-center" data-id="element-6966">
                        <input id={`filter-tasktype-${type}`} name={`filter-tasktype-${type}`} type="checkbox" checked={filters.taskTypes.includes(type)} onChange={() => toggleFilter('taskTypes', type)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-6967" />
                        <label htmlFor={`filter-tasktype-${type}`} className="ml-2 text-sm text-gray-700" data-id="element-6968">
                          {type}
                        </label>
                      </div>)}
                  </div>
                </div>

                {/* Module filters */}
                <div data-id="element-6969">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-6970">
                    Module
                  </h4>
                  <div className="space-y-2" data-id="element-6971">
                    {(['PO', 'VM', 'AP', 'CM', 'EM', 'DI', 'OCR', 'Financial', 'Settings'] as TaskModule[]).map(module => <div key={module} className="flex items-center" data-id="element-6972">
                        <input id={`filter-module-${module}`} name={`filter-module-${module}`} type="checkbox" checked={filters.modules.includes(module)} onChange={() => toggleFilter('modules', module)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-6973" />
                        <label htmlFor={`filter-module-${module}`} className="ml-2 text-sm text-gray-700" data-id="element-6974">
                          {module}
                        </label>
                      </div>)}
                  </div>
                </div>

                {/* Status filters */}
                <div data-id="element-6975">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-6976">
                    Status
                  </h4>
                  <div className="space-y-2" data-id="element-6977">
                    {(['Pending', 'In Progress', 'Completed', 'Cancelled', 'Overdue', 'Waiting Approval'] as TaskStatus[]).map(status => <div key={status} className="flex items-center" data-id="element-6978">
                        <input id={`filter-status-${status}`} name={`filter-status-${status}`} type="checkbox" checked={filters.statuses.includes(status)} onChange={() => toggleFilter('statuses', status)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-6979" />
                        <label htmlFor={`filter-status-${status}`} className="ml-2 text-sm text-gray-700 flex items-center" data-id="element-6980">
                          {getStatusBadge(status).icon}
                          {status}
                        </label>
                      </div>)}
                  </div>
                </div>

                {/* Priority filters */}
                <div data-id="element-6981">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-6982">
                    Priority
                  </h4>
                  <div className="space-y-2" data-id="element-6983">
                    {(['Urgent', 'High', 'Medium', 'Low'] as TaskPriority[]).map(priority => <div key={priority} className="flex items-center" data-id="element-6984">
                        <input id={`filter-priority-${priority}`} name={`filter-priority-${priority}`} type="checkbox" checked={filters.priorities.includes(priority)} onChange={() => toggleFilter('priorities', priority)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-6985" />
                        <label htmlFor={`filter-priority-${priority}`} className="ml-2 text-sm text-gray-700 flex items-center" data-id="element-6986">
                          {getPriorityBadge(priority).icon}
                          {priority}
                        </label>
                      </div>)}
                  </div>
                </div>
              </div>

              {/* Second row of filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" data-id="element-6987">
                {/* Assigned To filters */}
                <div data-id="element-6988">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-6989">
                    Assigned To
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto" data-id="element-6990">
                    {getAllAssignees().map(assignee => {
                  const task = tasks.find(t => t.assignedTo === assignee);
                  const isGroup = task?.assignedToType === 'group';
                  const Icon = isGroup ? UsersIcon : UserIcon;
                  return <div key={assignee} className="flex items-center" data-id="element-6991">
                          <input id={`filter-assignedto-${assignee}`} name={`filter-assignedto-${assignee}`} type="checkbox" checked={filters.assignedTo.includes(assignee)} onChange={() => toggleAssignedToFilter(assignee)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-6992" />
                          <label htmlFor={`filter-assignedto-${assignee}`} className="ml-2 text-sm text-gray-700 flex items-center" data-id="element-6993">
                            <Icon className={`h-3.5 w-3.5 mr-1 ${isGroup ? 'text-purple-500' : 'text-blue-500'}`} data-id="element-6994" />
                            {assignee}
                          </label>
                        </div>;
                })}
                  </div>
                </div>

                {/* Due date filters */}
                <div data-id="element-6995">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-6996">
                    Due Date
                  </h4>
                  <div className="space-y-2" data-id="element-6997">
                    <div data-id="element-6998">
                      <select value={filters.dateRange} onChange={e => {
                    setFilters({
                      ...filters,
                      dateRange: e.target.value
                    });
                  }} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-6999">
                        <option value="" data-id="element-7000">Any time</option>
                        <option value="today" data-id="element-7001">Due today</option>
                        <option value="tomorrow" data-id="element-7002">Due tomorrow</option>
                        <option value="this-week" data-id="element-7003">Due this week</option>
                        <option value="next-week" data-id="element-7004">Due next week</option>
                        <option value="this-month" data-id="element-7005">Due this month</option>
                        <option value="custom" data-id="element-7006">Custom range</option>
                      </select>
                    </div>
                    {filters.dateRange === 'custom' && <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-7007">
                        <div data-id="element-7008">
                          <label htmlFor="start-date" className="block text-xs font-medium text-gray-700" data-id="element-7009">
                            Start Date
                          </label>
                          <input type="date" id="start-date" value={filters.startDate} onChange={e => {
                      setFilters({
                        ...filters,
                        startDate: e.target.value
                      });
                    }} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-7010" />
                        </div>
                        <div data-id="element-7011">
                          <label htmlFor="end-date" className="block text-xs font-medium text-gray-700" data-id="element-7012">
                            End Date
                          </label>
                          <input type="date" id="end-date" value={filters.endDate} onChange={e => {
                      setFilters({
                        ...filters,
                        endDate: e.target.value
                      });
                    }} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-7013" />
                        </div>
                      </div>}
                  </div>
                </div>

                {/* Other filters */}
                <div data-id="element-7014">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-7015">
                    Other Filters
                  </h4>
                  <div className="space-y-2" data-id="element-7016">
                    <div className="flex items-center" data-id="element-7017">
                      <input id="filter-overdue" name="filter-overdue" type="checkbox" checked={filters.overdueOnly} onChange={() => {
                    setFilters({
                      ...filters,
                      overdueOnly: !filters.overdueOnly
                    });
                  }} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-7018" />
                      <label htmlFor="filter-overdue" className="ml-2 text-sm text-gray-700 flex items-center" data-id="element-7019">
                        <AlertTriangleIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-7020" />
                        Overdue only
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-7021">
                      <input id="filter-assigned-to-me" name="filter-assigned-to-me" type="checkbox" checked={filters.assignedToMe} onChange={() => {
                    setFilters({
                      ...filters,
                      assignedToMe: !filters.assignedToMe
                    });
                  }} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-7022" />
                      <label htmlFor="filter-assigned-to-me" className="ml-2 text-sm text-gray-700" data-id="element-7023">
                        Assigned to me
                      </label>
                    </div>
                  </div>

                  {/* Tags filter */}
                  <div className="mt-4" data-id="element-7024">
                    <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-7025">
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-2" data-id="element-7026">
                      {getAllTags().map(tag => <button key={tag} onClick={() => toggleTagFilter(tag)} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${filters.tags.includes(tag) ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-7027">
                          <TagIcon className="h-3 w-3 mr-1" data-id="element-7028" />
                          {tag}
                        </button>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tasks count and pagination info */}
          <div className="flex justify-between items-center mb-4" data-id="element-7029">
            <div className="text-sm text-gray-700" data-id="element-7030">
              Showing{' '}
              <span className="font-medium" data-id="element-7031">
                {filteredTasks.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}
              </span>{' '}
              to{' '}
              <span className="font-medium" data-id="element-7032">
                {Math.min(currentPage * pageSize, filteredTasks.length)}
              </span>{' '}
              of <span className="font-medium" data-id="element-7033">{filteredTasks.length}</span>{' '}
              tasks
            </div>
            <div className="flex items-center space-x-2" data-id="element-7034">
              <span className="text-sm text-gray-700" data-id="element-7035">Rows per page:</span>
              <select value={pageSize} onChange={e => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-7036">
                <option value={5} data-id="element-7037">5</option>
                <option value={10} data-id="element-7038">10</option>
                <option value={25} data-id="element-7039">25</option>
                <option value={50} data-id="element-7040">50</option>
              </select>
            </div>
          </div>

          {/* Tasks table */}
          {isLoading ? <div className="flex justify-center items-center py-20" data-id="element-7041">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" data-id="element-7042"></div>
            </div> : filteredTasks.length === 0 ? <div className="text-center py-10" data-id="element-7043">
              <ClipboardListIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-7044" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-7045">
                No tasks found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-7046">
                Try adjusting your search or filter criteria.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-7047">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-7048">
                <thead className="bg-gray-50" data-id="element-7049">
                  <tr data-id="element-7050">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('title')} data-id="element-7051">
                      <div className="flex items-center" data-id="element-7052">
                        Task
                        {sortConfig.key === 'title' && <span className="ml-1" data-id="element-7053">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-7054" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-7055" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('taskType')} data-id="element-7056">
                      <div className="flex items-center" data-id="element-7057">
                        Type
                        {sortConfig.key === 'taskType' && <span className="ml-1" data-id="element-7058">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-7059" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-7060" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('module')} data-id="element-7061">
                      <div className="flex items-center" data-id="element-7062">
                        Module
                        {sortConfig.key === 'module' && <span className="ml-1" data-id="element-7063">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-7064" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-7065" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('status')} data-id="element-7066">
                      <div className="flex items-center" data-id="element-7067">
                        Status
                        {sortConfig.key === 'status' && <span className="ml-1" data-id="element-7068">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-7069" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-7070" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('priority')} data-id="element-7071">
                      <div className="flex items-center" data-id="element-7072">
                        Priority
                        {sortConfig.key === 'priority' && <span className="ml-1" data-id="element-7073">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-7074" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-7075" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('dueDate')} data-id="element-7076">
                      <div className="flex items-center" data-id="element-7077">
                        Due Date
                        {sortConfig.key === 'dueDate' && <span className="ml-1" data-id="element-7078">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-7079" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-7080" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('assignedTo')} data-id="element-7081">
                      <div className="flex items-center" data-id="element-7082">
                        Assigned To
                        {sortConfig.key === 'assignedTo' && <span className="ml-1" data-id="element-7083">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-7084" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-7085" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7086">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-7087">
                  {getCurrentPageTasks().map(task => {
                const statusBadge = getStatusBadge(task.status);
                const priorityBadge = getPriorityBadge(task.priority);
                const moduleBadge = getModuleBadge(task.module);
                const taskTypeBadge = getTaskTypeBadge(task.taskType);
                const taskDueDate = new Date(task.dueDate);
                const isTaskOverdue = isOverdue(task.dueDate) && task.status !== 'Completed' && task.status !== 'Cancelled';
                return <tr key={task.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleTaskClick(task)} data-id="element-7088">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7089">
                          <div className="flex items-center" data-id="element-7090">
                            <div className="ml-0" data-id="element-7091">
                              <div className="text-sm font-medium text-gray-900" data-id="element-7092">
                                {task.title}
                              </div>
                              <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-7093">
                                {task.description}
                              </div>
                              {task.tags && <div className="flex flex-wrap gap-1 mt-1" data-id="element-7094">
                                  {task.tags.slice(0, 2).map(tag => <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-7095">
                                      <TagIcon className="h-3 w-3 mr-0.5" data-id="element-7096" />
                                      {tag}
                                    </span>)}
                                  {task.tags.length > 2 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-7097">
                                      +{task.tags.length - 2}
                                    </span>}
                                </div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7098">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${taskTypeBadge}`} data-id="element-7099">
                            {task.taskType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7100">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${moduleBadge}`} data-id="element-7101">
                            {task.module}
                          </span>
                          {task.relatedEntityName && <div className="text-xs text-gray-500 mt-1" data-id="element-7102">
                              {task.relatedEntityName}
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7103">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.bgColor} ${statusBadge.textColor}`} data-id="element-7104">
                            {statusBadge.icon}
                            {task.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7105">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityBadge.bgColor} ${priorityBadge.textColor}`} data-id="element-7106">
                            {priorityBadge.icon}
                            {task.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7107">
                          <div className="flex items-center" data-id="element-7108">
                            <CalendarIcon className={`h-4 w-4 mr-1 ${isTaskOverdue ? 'text-red-500' : 'text-gray-400'}`} data-id="element-7109" />
                            <span className={`text-sm ${isTaskOverdue ? 'text-red-500 font-medium' : 'text-gray-500'}`} data-id="element-7110">
                              {formatDate(task.dueDate)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7111">
                          {renderAssignedToBadge(task)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7112">
                          <div className="flex justify-end space-x-2" data-id="element-7113">
                            <button onClick={e => {
                        e.stopPropagation();
                        handleTaskClick(task);
                      }} className="text-indigo-600 hover:text-indigo-900" title="View task" data-id="element-7114">
                              <EyeIcon className="h-5 w-5" data-id="element-7115" />
                            </button>
                            {task.status !== 'Completed' && task.status !== 'Cancelled' && <button onClick={e => {
                        e.stopPropagation();
                        handleCompleteTask(task.id);
                      }} className="text-green-600 hover:text-green-900" title="Mark as complete" data-id="element-7116">
                                  <CheckIcon className="h-5 w-5" data-id="element-7117" />
                                </button>}
                            {task.status !== 'Cancelled' && task.status !== 'Completed' && <button onClick={e => {
                        e.stopPropagation();
                        handleCancelTask(task.id);
                      }} className="text-red-600 hover:text-red-900" title="Cancel task" data-id="element-7118">
                                  <XIcon className="h-5 w-5" data-id="element-7119" />
                                </button>}
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}

          {/* Pagination controls */}
          {filteredTasks.length > 0 && totalPages > 1 && <div className="mt-4 flex items-center justify-between" data-id="element-7120">
              <div className="flex-1 flex justify-between sm:hidden" data-id="element-7121">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-7122">
                  Previous
                </button>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-7123">
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-7124">
                <div data-id="element-7125">
                  <p className="text-sm text-gray-700" data-id="element-7126">
                    Page <span className="font-medium" data-id="element-7127">{currentPage}</span> of{' '}
                    <span className="font-medium" data-id="element-7128">{totalPages}</span>
                  </p>
                </div>
                <div data-id="element-7129">
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-7130">
                    <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-7131">
                      <span className="sr-only" data-id="element-7132">First</span>
                      <span className="h-5 w-5 flex justify-center items-center" aria-hidden="true" data-id="element-7133">
                        &laquo;
                      </span>
                    </button>
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-7134">
                      <span className="sr-only" data-id="element-7135">Previous</span>
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-7136" />
                    </button>
                    {Array.from({
                  length: Math.min(5, totalPages)
                }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-7137">
                            {pageNum}
                          </button>;
                })}
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-7138">
                      <span className="sr-only" data-id="element-7139">Next</span>
                      <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-7140" />
                    </button>
                    <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-7141">
                      <span className="sr-only" data-id="element-7142">Last</span>
                      <span className="h-5 w-5 flex justify-center items-center" aria-hidden="true" data-id="element-7143">
                        &raquo;
                      </span>
                    </button>
                  </nav>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}